import { Button, Dialog, IconButton, InputAdornment, Paper, Theme, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { makeStyles } from '@mui/styles';
import { AppFilterFields, FilterCellsProps, FilterProps, ValuesOfAny } from '../../utils/interfaces';
import CloseIcon from '@mui/icons-material/Close';
import { DatePicker } from '@mui/x-date-pickers';
import KenColors from '../../utils/kenColors';
import KenSelect from '../KenSelect';
import moment from 'moment';
import { Box } from '@mui/system';

const useStyles = makeStyles((theme: Theme) => ({
  filterFooter: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    margin: '10px 0px',
    gap: '10px',
  },
  filterCellsContainer: {
    display: 'flex',
    padding: '7px 14px',
    alignItems: 'center',
    gap: '6px',
    borderRadius: '20px !important',
    background: `${theme.palette.KenColors.ClientBlue4} !important`,
    boxShadow: 'none !important',
    [theme.breakpoints.down('md')]: {
      padding: '7px 14px',
      gap: '3px',
    },
  },
  inputLableCustom: {
    color: `${theme.palette.KenColors.ClientBlack2} !important`,
    fontSize: '16px !important',
    fontWeight: '500 !important',
    margin: '6px 0px !important',
  },
  inputStyles: {
    borderRadius: '8px !important',
    fontSize: '16px !important',
  },
}));

export const FilterCells: React.FunctionComponent<FilterCellsProps> = ({ handleFilerRemove, cellsArray }) => {
  const classes = useStyles();

  return Object.keys(cellsArray)?.map((items, index) => {
    return (
      <Paper key={index} className={classes.filterCellsContainer}>
        <Typography color={KenColors.ClientBlue5} fontWeight={'500'} whiteSpace={'nowrap'} fontSize={'0.8rem'}>
          {cellsArray[items]}
        </Typography>
        <IconButton
          style={{ padding: '0px' }}
          onClick={() => {
            handleFilerRemove(items);
          }}
        >
          <CloseIcon sx={{ height: { xs: '14px', md: '18px' }, color: KenColors.ClientBlue5 }} />
        </IconButton>
      </Paper>
    );
  });
};

interface AppFilterProps extends FilterProps {
  handleClearAll?: () => void;
}

const AppFilter: React.FunctionComponent<AppFilterProps> = ({ handleClose, open, handleFilterSubmit, dependencyArray, handleChange, filterData, transform, handleClearAll = () => {} }) => {
  const method = useForm<AppFilterFields>();
  const classes = useStyles();
  const { register, handleSubmit, control, reset, setValue } = method;

  const handleFiltering: SubmitHandler<AppFilterFields> = (data) => {
    handleFilterSubmit(data);
  };

  const [fieldTypeSwitcher, setFieldTypeSwitcher] = useState<ValuesOfAny>();

  const handleInputFieldSwitcher = (name: string, value: boolean) => {
    setFieldTypeSwitcher({ ...fieldTypeSwitcher, [name]: value });
  };

  // Filterdata clearing on data change
  useEffect(() => {
    reset();
    Object.keys(filterData).forEach((items) => {
      setValue(items, filterData[items]);
    });
  }, [filterData, setValue, reset]);

  return (
    <Dialog
      open={open}
      onClose={() => {
        handleClose();
      }}
      hideBackdrop={true}
      fullWidth
      PaperProps={{ style: {boxShadow:"none",border:"1px solid #e2e2e2", width: '348px', position: 'absolute', top: '55%', right: '27%', borderRadius: '24px', transform: `translate(${transform.left}, ${transform.top})`, padding: '24px' } }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Typography fontWeight={'600'} fontSize={'18px'} margin={'10px 0px'}>
          Filters
        </Typography>
        <IconButton
          size="small"
          onClick={handleClose}
          sx={{ marginLeft: 'auto' }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <FormProvider {...method}>
        <form style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', gap: '14px' }} onSubmit={handleSubmit(handleFiltering)}>
          {dependencyArray?.map((items: any, index: React.Key | null | undefined) => {
            switch (items.type) {
              case 'date':
                return (
                  <Controller
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => {
                      return (
                        <div style={{ ...items.fieldStyle }}>
                          <DatePicker
                            {...items}
                            value={value ? moment(moment(value).format('YYYY-MM-DD').toString()) : null}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              onChange(e);
                              handleChange(moment(e as any).format('YYYY-MM-DD'), items.name);
                            }}
                            onBlur={() => {
                              handleInputFieldSwitcher(`${items.name}`, false);
                            }}
                            sx={{ width: '100%', margin: '4px 0px' }}
                          />
                        </div>
                      );
                    }}
                    {...register(`${items.name}`, { ...items?.validation })}
                  />
                );
              case 'select':
                return items.options && items.options?.length > 0 ? (
                  <Controller
                    key={index}
                    control={control}
                    render={({ field: { onChange, value } }) => {
                      return (
                        <KenSelect
                          selectLabel={items.label}
                          border
                          customMargin
                          options={items.options}
                          value={value}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            onChange(e);
                            handleChange(e.target.value, items.name);
                          }}
                        />
                      );
                    }}
                    {...register(items.name)}
                  />
                ) : null;
              default:
                return null;
            }
          })}
          <div className={classes.filterFooter}>
            <Button
              sx={{ textTransform: 'capitalize', fontSize: '15px', fontWeight: '600', width: { xs: '90px', sm: '100px', md: '120px' }, boxShadow: 'none', height: '36px' }}
              variant="text"
              size="medium"
              onClick={() => {
                reset();
                handleClearAll();
                handleClose();
              }}
            >
              Clear
            </Button>
            <Button
              sx={{
                textTransform: 'capitalize',
                fontSize: '15px',
                fontWeight: '600',
                padding: { xs: '8px', sm: '10px', md: '12px' },
                borderRadius: '8px',
                width: { xs: '90px', sm: '100px', md: '120px' },
                boxShadow: 'none !important',
                height: '36px',
              }}
              variant="contained"
              size="medium"
              type="submit"
            >
              Apply
            </Button>
          </div>
        </form>
      </FormProvider>
    </Dialog>
  );
};

export default AppFilter;
